import { useCallback, useRef, useState } from 'react'
import SeoHead from '../components/SeoHead'
import { Link } from 'react-router-dom'
import { api, ApiError, base64ToBlob, fileToBase64, triggerDownload } from '../lib/api'

type Format = 'CAdES' | 'XAdES'
type XadesVariant = 'Enveloped' | 'Enveloping' | 'Detached'
type Status = 'idle' | 'reading' | 'signing' | 'success' | 'error'

interface SuccessResult {
  signerSubject:    string
  signerThumbprint: string
  outputName:       string
  outputBytes:      number
  blob:             Blob
}

export default function SignPage() {
  const [file,     setFile]     = useState<File | null>(null)
  const [format,   setFormat]   = useState<Format>('CAdES')
  const [variant,  setVariant]  = useState<XadesVariant>('Enveloped')
  const [detached, setDetached] = useState(true)
  const [status,   setStatus]   = useState<Status>('idle')
  const [result,   setResult]   = useState<SuccessResult | null>(null)
  const [error,    setError]    = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleFile = (f: File) => {
    setFile(f)
    setStatus('idle')
    setResult(null)
    setError(null)
    if (format === 'XAdES' && !f.name.toLowerCase().endsWith('.xml')) {
      setVariant('Enveloping')
    }
  }

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) handleFile(f)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format])

  const onSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) handleFile(f)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format])

  const onSign = useCallback(async () => {
    if (!file) return
    setStatus('reading')
    setError(null)
    setResult(null)
    try {
      const base64 = await fileToBase64(file)
      setStatus('signing')

      if (format === 'CAdES') {
        const resp = await api.signCades({
          contentBase64:    base64,
          certificateAlias: 'demo',
          profile:          'BES',
          detached,
        })
        const ext  = detached ? '.p7s' : '.imz'
        const blob = base64ToBlob(resp.signedCms, 'application/pkcs7-signature')
        setResult({
          signerSubject:    resp.signerSubject,
          signerThumbprint: resp.signerThumbprint,
          outputName:       baseName(file.name) + ext,
          outputBytes:      blob.size,
          blob,
        })
      } else {
        const documentUri = variant === 'Detached' ? file.name : undefined
        const resp = await api.signXades({
          contentBase64:    base64,
          certificateAlias: 'demo',
          variant,
          documentUri,
        })
        const blob = base64ToBlob(resp.signedXml, 'application/xml')
        setResult({
          signerSubject:    resp.signerSubject,
          signerThumbprint: resp.signerThumbprint,
          outputName:       baseName(file.name) + '.xades.xml',
          outputBytes:      blob.size,
          blob,
        })
      }
      setStatus('success')
    } catch (err) {
      setStatus('error')
      setError(err instanceof ApiError ? `${err.code}: ${err.message}` : String(err))
    }
  }, [file, format, variant, detached])

  return (
    <>
      <SeoHead
        title="İmzalama demosu — CAdES, XAdES ve PAdES çıktısını deneyin"
        description="Dosyanızı yükleyin, format seçin, imzalı çıktıyı indirin. Demo sunucu sertifikasıyla imzalanır — teknik değerlendirme içindir, hukuki bağlayıcılığı yoktur. Nitelikli imza için token akışını kullanın."
        path="/sign"
      />

      <section className="container-page py-12 md:py-16">
        <h1 className="text-3xl md:text-4xl font-semibold">İmzala — Demo</h1>
        <p className="mt-2 text-[var(--color-fg-muted)] max-w-2xl">
          Dosyanı yükle, format seç, imzayı indir. Sunucudaki demo sertifika ile imzalanır;
          gerçek hayatta CAdES, XAdES veya PAdES olarak hangisini istiyorsan onu üretir.
        </p>

        <div className="card mt-4 border-amber-200 bg-amber-50">
          <h3 className="font-semibold text-amber-900">Demo modu</h3>
          <ul className="mt-2 list-disc pl-5 text-sm text-amber-800 space-y-1">
            <li>İmza <strong>self-signed demo cert</strong> ile üretilir — hukuki bağlayıcılığı yok, sadece teknik test için.</li>
            <li>IP başına <strong>saatte 5 imza</strong> sınırı var.</li>
            <li>Yüklenen dosya sunucuda saklanmaz; imzalanıp anında geri döner.</li>
            <li>Gerçek kurum mührü / mali mühür ile imza ya da kurumsal entegrasyon için <Link to="/destek" className="underline">bizimle iletişime geçin</Link>.</li>
          </ul>
        </div>

        <div
          className={`mt-8 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
            dragOver ? 'border-[var(--color-accent)] bg-[var(--color-accent-wash)]'
                     : 'border-[var(--color-border)] bg-[var(--color-bg-elev)]'
          }`}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          {!file ? (
            <>
              <p className="text-base font-medium">Dosyayı buraya sürükle</p>
              <p className="mt-1 text-sm text-[var(--color-fg-muted)]">veya</p>
              <button onClick={() => inputRef.current?.click()} type="button" className="btn-primary mt-3">
                Dosya seç
              </button>
            </>
          ) : (
            <>
              <p className="font-mono text-sm">{file.name}</p>
              <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{formatBytes(file.size)}</p>
              <button onClick={() => inputRef.current?.click()} type="button" className="btn-ghost mt-3 text-xs">
                Başka dosya seç
              </button>
            </>
          )}
          <input ref={inputRef} type="file" onChange={onSelect} className="hidden" />
        </div>

        {file && (
          <div className="card mt-6">
            <h2 className="text-lg font-semibold">İmza ayarları</h2>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <RadioGroup
                label="Format"
                value={format}
                options={[
                  { value: 'CAdES', label: 'CAdES (CMS / PKCS#7) — her dosya türü' },
                  { value: 'XAdES', label: 'XAdES (XML imza) — XML belgeler' },
                ]}
                onChange={v => setFormat(v as Format)}
              />

              {format === 'CAdES' && (
                <RadioGroup
                  label="Tip"
                  value={detached ? 'detached' : 'attached'}
                  options={[
                    { value: 'detached', label: 'Detached (.p7s) — orijinal dosya ayrı' },
                    { value: 'attached', label: 'Attached (.imz) — içerik gömülü (TÜBİTAK İmzager uyumlu)' },
                  ]}
                  onChange={v => setDetached(v === 'detached')}
                />
              )}

              {format === 'XAdES' && (
                <RadioGroup
                  label="Variant"
                  value={variant}
                  options={[
                    { value: 'Enveloped',  label: 'Enveloped — XML belgenin içine eklenir' },
                    { value: 'Enveloping', label: 'Enveloping — içerik ds:Object\'a gömülür' },
                    { value: 'Detached',   label: 'Detached — URI ile referans' },
                  ]}
                  onChange={v => setVariant(v as XadesVariant)}
                />
              )}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={onSign}
                disabled={status === 'reading' || status === 'signing'}
                className="btn-primary"
                type="button"
              >
                {status === 'reading'  ? 'Dosya okunuyor…'
                 : status === 'signing' ? 'İmzalanıyor…'
                 : 'İmzala ve indir'}
              </button>
              <span className="text-xs text-[var(--color-fg-muted)]">
                Demo sertifikası ile imzalanır.
              </span>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="card mt-6 border-red-200 bg-red-50">
            <h3 className="font-semibold text-red-800">Hata</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            {error?.includes('RATE_LIMITED') && (
              <p className="mt-2 text-xs text-red-700">
                Demo sınırı aşıldı; aynı IP'den saatte 5 imza. Kurumsal kullanım için
                bizimle iletişime geçin.
              </p>
            )}
          </div>
        )}

        {status === 'success' && result && (
          <div className="card mt-6 border-emerald-200 bg-emerald-50">
            <h3 className="text-lg font-semibold text-emerald-900">İmza üretildi</h3>
            <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <KV label="İmzalayan"  value={result.signerSubject} />
              <KV label="Parmak izi" value={result.signerThumbprint} mono />
              <KV label="Çıktı"      value={result.outputName} mono />
              <KV label="Boyut"      value={formatBytes(result.outputBytes)} />
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => triggerDownload(result.blob, result.outputName)}
                className="btn-primary"
                type="button"
              >
                İndir ({result.outputName})
              </button>
              <Link to="/verify" className="btn-ghost">Doğrulayıcıda aç →</Link>
            </div>
            <p className="mt-4 text-xs text-emerald-800">
              İmza üretildi ama bu demo cert ile hukuki bağlayıcılığı yoktur. Doğrulayıcı
              sayfasında dosyayı açıp imza yapısını inceleyebilirsin.
            </p>
          </div>
        )}

      </section>
    </>
  )
}

function RadioGroup({ label, value, options, onChange }: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">{label}</p>
      <div className="mt-2 space-y-2">
        {options.map(opt => (
          <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name={label}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="mt-0.5 accent-[var(--color-accent)]"
            />
            <span className="text-sm">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-emerald-700 text-xs">{label}</p>
      <p className={`mt-0.5 text-emerald-900 ${mono ? 'font-mono break-all' : ''}`}>{value}</p>
    </div>
  )
}

function baseName(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.substring(0, dot) : name
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}
